import React, { useState } from 'react';

function ContactUs() {
  const [status, setStatus] = useState("");
  // Error state hooks for individual inputs
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Strips away everything except numeric digits
    const cleanNumber = phone.replace(/\D/g, "");
    // Returns true only if it is exactly 10 digits long
    return cleanNumber.length === 10;
  };

  const handlePhoneKeyDown = (e) => {
    const cleanNumber = e.target.value.replace(/\D/g, "");
    // If they already have 10 digits, prevent typing any more numbers
    // (Still allows Backspace, Delete, Arrow keys, etc.)
    if (cleanNumber.length >= 10 && /\d/.test(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  const handlePhonePaste = (e) => {
    const pasteData = (e.clipboardData || window.clipboardData).getData('text');
    const cleanPaste = pasteData.replace(/\D/g, "");
    const cleanExisting = e.target.value.replace(/\D/g, "");
    
    // Calculate space remaining to hit the 10-digit maximum limit
    const allowedLength = 10 - cleanExisting.length;
    
    if (cleanPaste.length > allowedLength) {
      e.preventDefault();
      // Slice and insert only what fits
      const trimmedPaste = cleanPaste.slice(0, allowedLength);
      document.execCommand("insertText", false, trimmedPaste);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous error messages
    setEmailError("");
    setPhoneError("");
    setStatus("");

    const formData = new FormData(e.target);
    const emailVal = formData.get("Secure Email Address");
    const phoneVal = formData.get("Mobile Number");

    let isValid = true;

    // 1. Email Validation
    if (!validateEmail(emailVal)) {
      setEmailError("Please enter a valid secure email address.");
      isValid = false;
    }

    // 2. Mobile Validation (Only validates if the user actually typed something)
    if (phoneVal && !validatePhone(phoneVal)) {
      setPhoneError("Mobile number must be exactly 10 digits.");
      isValid = false;
    }

    // Terminate pipeline sequence early if validations fail
    if (!isValid) return;

    setStatus("Sending...");

    // Web3Forms public access key configuration
    formData.append("access_key", "1fc1d214-40a6-4840-954b-e3390a9a036a");
    
    // Setting up secondary notification routing
    formData.append("cc", "naresh.kokkonda@nirvanasol.com"); 
    
    // Dynamically inject the Enterprise Name into the email Subject heading
    const enterpriseName = formData.get("Enterprise Name") || "New Enterprise";
    formData.append("subject", `NirvanaSol Ingestion Request - From: ${enterpriseName}`);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setStatus("Connection Initiated Successfully!");
        e.target.reset();
      } else {
        setStatus("Submission Error. Please contact dispatch via direct email.");
      }
    } catch (error) {
      setStatus("Network error. Please attempt direct dispatch.");
    }
  };

  return (
    <div className="page">
      {/* Scope-contained style element for seamless typographic alignment and interactions */}
      <style dangerouslySetInnerHTML={{ __html: `
        .page {
          background-color: #0b0f19;
          color: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
          padding: 100px 20px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .contact-container {
          max-width: 900px;
          width: 100%;
          text-align: left;
        }

        /* Elements start transparent and slide up slightly */
        .animate-item {
          opacity: 0;
          transform: translateY(25px);
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        /* Explicit execution staggered intervals */
        .brand-title { animation-delay: 0.1s; }
        .brand-intro { animation-delay: 0.22s; }
        .contact-layout { animation-delay: 0.35s; }

        .brand-title {
          font-size: 3.5rem;
          font-weight: 800;
          margin-bottom: 25px;
          letter-spacing: -0.03em;
          background: linear-gradient(to right, #38bdf8, #2563eb);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .brand-intro {
          font-size: 1.25rem;
          line-height: 1.75;
          color: #cbd5e1;
          margin-bottom: 50px;
          font-weight: 400;
          max-width: 700px;
        }

        /* Two column layout splitting form and details */
        .contact-layout {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 50px;
        }

        @media (max-width: 768px) {
          .contact-layout {
            grid-template-columns: 1fr;
            gap: 40px;
          }
        }

        /* Form styling */
        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .form-group input, .form-group textarea {
          background-color: #121826;
          border: 1px solid #1e293b;
          border-radius: 8px;
          padding: 14px 16px;
          color: #ffffff;
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input:focus, .form-group textarea:focus {
          outline: none;
          border-color: #38bdf8;
          box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.15);
        }

        .submit-btn {
          background: linear-gradient(to right, #38bdf8, #2563eb);
          color: #ffffff;
          border: none;
          border-radius: 8px;
          padding: 14px 24px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s ease, opacity 0.2s ease;
          margin-top: 10px;
        }

        .submit-btn:hover {
          transform: translateY(-1px);
          opacity: 0.95;
        }

        .status-message {
          font-size: 1rem;
          font-weight: 500;
          color: #38bdf8;
          margin-top: 5px;
        }

        .error-message {
          font-size: 0.85rem;
          font-weight: 500;
          color: #f87171;
          margin-top: -2px;
        }

        /* Information column blocks */
        .info-sidebar {
          display: flex;
          flex-direction: column;
          gap: 35px;
        }

        .info-block h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 10px;
        }

        .info-block p {
          font-size: 1.05rem;
          line-height: 1.6;
          color: #94a3b8;
          margin: 0;
        }

        .info-block a {
          color: #38bdf8;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .info-block a:hover {
          color: #2563eb;
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      ` }} />

      <div className="contact-container">
        <h1 className="brand-title animate-item">Connect With Us</h1>
        <p className="brand-intro animate-item">
          Let’s discuss how NirvanaSol can architect your next digital infrastructure milestone. Reach out via the terminal layout below or drop an direct dispatch.
        </p>

        <div className="contact-layout animate-item">
          {/* Left Column: Form */}
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Enterprise Name</label>
              <input type="text" id="name" name="Enterprise Name" required placeholder="Your name or company" />
            </div>

            <div className="form-group">
              <label htmlFor="email">Secure Email Address</label>
              <input type="email" id="email" name="Secure Email Address" required placeholder="name@company.com" />
              {emailError && <div className="error-message">{emailError}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Mobile Number</label>
              <input 
                type="tel" 
                id="phone" 
                name="Mobile Number" 
                placeholder="XXXXXXXXXX" 
                onKeyDown={handlePhoneKeyDown}
                onPaste={handlePhonePaste}
              />
              {phoneError && <div className="error-message">{phoneError}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="message">Project Brief / Inquiries</label>
              <textarea id="message" name="Project Brief / Inquiries" rows="5" required placeholder="Describe your architecture or workflow bottleneck..."></textarea>
            </div>

            <button type="submit" className="submit-btn">Initiate Connection</button>
            {status && <div className="status-message">{status}</div>}
          </form>

          <div className="info-sidebar">
            {/* Engineering Team Profiles */}
            <div className="info-block" style={{ borderLeft: '2px solid #38bdf8', paddingLeft: '15px', marginBottom: '5px' }}>
              <h3 style={{ fontSize: '1rem', color: '#38bdf8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '8px' }}>Lead Technical Architect</h3>
              <p style={{ color: '#ffffff', fontWeight: '700', fontSize: '1.2rem', marginBottom: '4px' }}>
                Jagan Valabhoju
              </p>
              <p style={{ fontSize: '0.95rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                Senior .NET Consultant & Full-Stack Engineer <br />
                <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Specializing in Distributed Systems & Web API Architecture</span>
              </p>
            </div>

            <div className="info-block" style={{ borderLeft: '2px solid #2563eb', paddingLeft: '15px', marginBottom: '15px' }}>
              <h3 style={{ fontSize: '1rem', color: '#2563eb', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '8px' }}>Database & Quality Systems</h3>
              <p style={{ color: '#ffffff', fontWeight: '700', fontSize: '1.2rem', marginBottom: '4px' }}>
                Naresh Kumar Kokkonda
              </p>
              <p style={{ fontSize: '0.95rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                Database Architect & QA Lead Engineer <br />
                <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Specializing in UI Frameworks, Relational Modeling & Test Automation</span>
              </p>
            </div>

            <hr style={{ border: '0', borderTop: '1px solid #1e293b', margin: '15px 0' }} />

            <div className="info-block">
              <h3>Direct Dispatches</h3>
              <p><a href="mailto:jagan.valabhoju@nirvanasol.com">jagan.valabhoju@nirvanasol.com</a></p>
              <p><a href="mailto:naresh.kokkonda@nirvanasol.com">naresh.kokkonda@nirvanasol.com</a></p>
            </div>

            <div className="info-block">
              <h3>Operational Base</h3>
              <p>
                Fully Remote Architecture<br />
                Hyderabad,<br />
                TG, India
              </p>
            </div>

            <div className="info-block">
              <h3>Response Paradigm</h3>
              <p>Technical assessments and ingestion pipeline queries are processed within 24 business hours.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;